import { Sparkles, MapPin, Repeat, BellRing, Globe, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COLOUR_KEYS, colourOf, REPEAT_LABELS, ALERT_LABELS } from "./palette";
import { RemindPicker } from "./RemindPicker";

const ZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "America/New_York", "Asia/Ho_Chi_Minh", "Asia/Tokyo"];
  }
})();

const selectCls =
  "min-h-[40px] rounded-xl border border-white/10 bg-panel px-3 text-xs text-ink outline-none";

export const EventForm = ({ form, setForm, onSave, editing, ideas, ideasLoading, onIdeas, accent, homeZone,
                           dryWindows }) => {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-page p-3" data-testid="event-form">
      {form.outdoors && dryWindows?.length > 0 && (
        <div className="mb-3 rounded-lg border border-white/10 p-2.5" data-testid="dry-windows">
          <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest"
             style={{ color: accent }}>
            <Sun className="h-3.5 w-3.5" /> Driest hours that day
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {dryWindows.slice(0, 4).map((w) => (
              <button
                key={`${w.from}-${w.to}`}
                data-testid={`dry-window-${w.from}`}
                onClick={() => set({ all_day: false, start: w.from, end: w.to })}
                className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                style={{ borderColor: accent, color: accent, backgroundColor: `${accent}14` }}
              >
                {w.from} – {w.to} · {w.hours}h dry
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest"
           style={{ color: accent }}>
          <Sparkles className="h-3.5 w-3.5" /> One tap ideas
        </p>
        <button
          data-testid="event-ideas-refresh"
          onClick={onIdeas}
          disabled={ideasLoading}
          className="text-xs text-ink-soft underline decoration-dotted hover:text-ink"
        >
          {ideasLoading ? "Thinking…" : ideas.length ? "More ideas" : "Suggest for me"}
        </button>
      </div>
      {ideas.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="event-ideas">
          {ideas.map((idea) => (
            <button
              key={idea.title}
              data-testid={`event-idea-${idea.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => set({ title: idea.title, all_day: idea.all_day !== false,
                                   colour: idea.kind === "holiday" ? "violet" : form.colour })}
              className="rounded-full border px-3 py-1.5 text-xs transition-colors hover:text-ink"
              style={idea.kind === "holiday"
                ? { borderColor: "rgba(139,92,246,.45)", color: "#c4b5fd", backgroundColor: "rgba(139,92,246,.12)" }
                : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
            >
              {idea.title}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          data-testid="event-title-input"
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
          maxLength={120}
          placeholder="e.g. Dentist appointment"
          className="min-h-[44px] flex-1 rounded-xl border border-white/10 bg-panel px-4 text-sm outline-none placeholder:text-ink-soft"
        />
        <button
          data-testid="event-allday-toggle"
          onClick={() => set({ all_day: !form.all_day })}
          className="rounded-full border px-4 py-2 text-xs font-semibold"
          style={form.all_day
            ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
            : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
        >
          All day
        </button>
        <button
          data-testid="event-outdoors-toggle"
          onClick={() => set({ outdoors: !form.outdoors })}
          aria-pressed={Boolean(form.outdoors)}
          className="rounded-full border px-4 py-2 text-xs font-semibold"
          style={form.outdoors
            ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
            : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
        >
          Outdoors
        </button>
        {!form.all_day && (
          <>
            <input
              data-testid="event-start-input"
              type="time"
              value={form.start}
              onChange={(e) => set({ start: e.target.value })}
              className="min-h-[44px] rounded-xl border border-white/10 bg-panel px-3 text-sm outline-none"
            />
            <input
              data-testid="event-end-input"
              type="time"
              value={form.end}
              onChange={(e) => set({ end: e.target.value })}
              className="min-h-[44px] rounded-xl border border-white/10 bg-panel px-3 text-sm outline-none"
            />
          </>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-panel px-3">
          <MapPin className="h-3.5 w-3.5 text-ink-soft" />
          <input
            data-testid="event-location-input"
            value={form.location}
            onChange={(e) => set({ location: e.target.value })}
            maxLength={160}
            placeholder="Where is it?"
            className="min-h-[40px] w-full bg-transparent text-sm outline-none placeholder:text-ink-soft"
          />
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Repeat className="h-3.5 w-3.5 text-ink-soft" />
          <select
            data-testid="event-repeat-select"
            value={form.repeat}
            onChange={(e) => set({ repeat: e.target.value })}
            className={selectCls}
          >
            {Object.entries(REPEAT_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <BellRing className="h-3.5 w-3.5 text-ink-soft" />
          <select
            data-testid="event-alert-select"
            value={form.alert}
            onChange={(e) => set({ alert: e.target.value })}
            className={selectCls}
          >
            {Object.entries(ALERT_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </span>
        <RemindPicker
          value={form.remind}
          onChange={(remind) => set({ remind })}
          accent={accent}
          testid="event-remind"
        />
        <span className="inline-flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5 text-ink-soft" />
          <select
            data-testid="event-timezone-select"
            value={form.tz || ""}
            onChange={(e) => set({ tz: e.target.value })}
            className={selectCls}
          >
            <option value="">{homeZone ? `My time (${homeZone.split("/").pop().replace(/_/g, " ")})` : "My own time"}</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>{z.replace(/_/g, " ")}</option>
            ))}
          </select>
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="event-colours">
        {COLOUR_KEYS.map((c) => (
          <button
            key={c}
            data-testid={`event-colour-${c}`}
            onClick={() => set({ colour: c })}
            aria-label={c}
            title={c}
            className="h-6 w-6 rounded-full transition-transform hover:scale-110"
            style={{
              backgroundColor: colourOf(c),
              boxShadow: form.colour === c ? "0 0 0 2px rgba(255,255,255,.8)" : "none",
              opacity: form.colour === c ? 1 : 0.72,
            }}
          />
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          data-testid="event-notes-input"
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
          maxLength={1000}
          placeholder="Notes (optional)"
          className="min-h-[40px] flex-1 rounded-xl border border-white/10 bg-panel px-4 text-sm outline-none placeholder:text-ink-soft"
        />
        <Button
          data-testid="event-save"
          onClick={onSave}
          className="min-h-[40px] rounded-full px-5 text-sm text-page hover:opacity-90 active:scale-95"
          style={{ backgroundColor: accent }}
        >
          {editing ? "Save event" : "Add event"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-ink-soft">Tip: drag an event onto another day to copy it.</p>
    </div>
  );
};
